import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { useNavigate, useParams, Link } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { firestore } from "../firebase";
import SEO from "./SEO";
import Modal from "react-modal";

/* ---------- Page ---------- */

const Page = styled.main`
  min-height: 70vh;
  padding: 40px 18px;
  background: radial-gradient(1200px 400px at 20% 0%, rgba(15, 118, 110, 0.12), transparent 55%),
    radial-gradient(900px 360px at 90% 10%, rgba(195, 122, 71, 0.14), transparent 55%),
    #f7fafc;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

/* ---------- Hero ---------- */

const Hero = styled.header`
  position: relative;
  border-radius: 22px;
  overflow: hidden;
  border: 1px solid #e5e7eb;
  box-shadow: 0 18px 55px rgba(0, 0, 0, 0.10);
  background: #0b1220;
  margin-bottom: 18px;
`;

const HeroBg = styled.div`
  position: absolute;
  inset: 0;
  background: ${({ $img }) => ($img ? `url(${$img})` : "none")};
  background-size: cover;
  background-position: center;
  filter: ${({ $img }) => ($img ? "saturate(1.05) contrast(1.02)" : "none")};
  transform: scale(1.05);
  opacity: ${({ $img }) => ($img ? 1 : 0)};
`;

const HeroOverlay = styled.div`
  position: absolute;
  inset: 0;
  background:
    linear-gradient(180deg, rgba(11, 18, 32, 0.20) 0%, rgba(11, 18, 32, 0.92) 78%),
    radial-gradient(900px 360px at 20% 0%, rgba(15, 118, 110, 0.30), transparent 60%),
    radial-gradient(900px 360px at 90% 10%, rgba(195, 122, 71, 0.25), transparent 60%);
`;

const HeroInner = styled.div`
  position: relative;
  padding: 22px;

  @media (min-width: 900px) {
    padding: 26px 28px;
  }
`;

const Breadcrumbs = styled.nav`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
  font-weight: 900;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.78);

  a {
    color: rgba(255, 255, 255, 0.88);
    text-decoration: none;
  }
  a:hover {
    text-decoration: underline;
  }
`;

const Title = styled.h1`
  margin: 12px 0 0;
  font-size: clamp(24px, 3.2vw, 46px);
  color: #fff;
  font-weight: 950;
  line-height: 1.12;
  letter-spacing: 0.2px;
`;

const MetaRow = styled.div`
  margin-top: 12px;
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  align-items: center;
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;

  padding: 8px 12px;
  border-radius: 999px;

  font-weight: 950;
  font-size: 12px;

  color: rgba(255, 255, 255, 0.92);
  background: rgba(255, 255, 255, 0.10);
  border: 1px solid rgba(255, 255, 255, 0.14);
`;

const ChipStrong = styled(Chip)`
  background: rgba(15, 118, 110, 0.20);
  border-color: rgba(15, 118, 110, 0.35);
`;

/* ---------- Content Card ---------- */

const Card = styled.section`
  background: #fff;
  border: 1px solid #e5e7eb;
  box-shadow: 0 14px 44px rgba(0, 0, 0, 0.07);
  border-radius: 22px;
  overflow: hidden;
`;

const Body = styled.div`
  padding: 18px;

  display: grid;
  gap: 18px;
  grid-template-columns: 1.45fr 0.85fr;

  @media (max-width: 950px) {
    grid-template-columns: 1fr;
  }
`;

const Content = styled.article`
  color: #374151;
  line-height: 1.9;
  font-size: 15.5px;

  p {
    margin: 12px 0;
  }

  a {
    color: #0f766e;
    font-weight: 950;
    text-decoration: none;
  }

  a:hover {
    text-decoration: underline;
  }

  h2,
  h3 {
    color: #111827;
    margin: 22px 0 10px;
    line-height: 1.2;
  }

  ul,
  ol {
    margin: 12px 0 12px 18px;
  }

  blockquote {
    margin: 14px 0;
    padding: 12px 14px;
    border-left: 4px solid rgba(15, 118, 110, 0.5);
    background: rgba(15, 118, 110, 0.06);
    border-radius: 12px;
    color: #1f2937;
    font-weight: 650;
  }
`;

const MediaCol = styled.aside`
  display: flex;
  flex-direction: column;
  gap: 12px;

  @media (min-width: 950px) {
    position: sticky;
    top: 92px;
    align-self: start;
  }

  @media (max-width: 950px) {
    order: -1; /* bilder först på mobil */
  }
`;

const ImgBtn = styled.button`
  border: 1px solid #e5e7eb;
  padding: 0;
  background: #000;
  border-radius: 18px;
  overflow: hidden;
  cursor: zoom-in;
  box-shadow: 0 12px 34px rgba(0, 0, 0, 0.08);
  transition: transform 160ms ease, box-shadow 160ms ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 18px 50px rgba(0, 0, 0, 0.12);
  }
`;

const Image = styled.img`
  width: 100%;
  height: auto;
  display: block;
  max-height: 360px;
  object-fit: cover;

  @media (max-width: 950px) {
    max-height: 520px;
  }
`;

const MediaHint = styled.div`
  margin-top: -4px;
  font-size: 12px;
  color: #6b7280;
  font-weight: 850;
`;

/* ---------- Actions ---------- */

const Actions = styled.div`
  padding: 14px 18px 18px;
  border-top: 1px solid #e5e7eb;

  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;

  @media (max-width: 700px) {
    justify-content: center;
  }
`;

const ActionsLeft = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: center;
`;

const Btn = styled.button`
  appearance: none;
  border: 1px solid #e5e7eb;
  background: #fff;
  color: #111827;
  font-weight: 950;
  border-radius: 999px;
  padding: 10px 14px;
  cursor: pointer;
  transition: transform 140ms ease, background 140ms ease;

  &:hover {
    background: #f9fafb;
    transform: translateY(-1px);
  }

  &:focus-visible {
    outline: 2px solid rgba(15, 118, 110, 0.45);
    outline-offset: 3px;
  }
`;

const GhostLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;

  padding: 10px 14px;
  border-radius: 999px;
  border: 1px solid #e5e7eb;
  background: #fff;
  color: #111827;
  font-weight: 950;
  text-decoration: none;

  &:hover {
    background: #f9fafb;
    transform: translateY(-1px);
  }
`;

const PrimaryLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;

  padding: 10px 14px;
  border-radius: 999px;
  border: 1px solid rgba(15, 118, 110, 0.25);
  background: rgba(15, 118, 110, 0.10);
  color: #0f766e;
  font-weight: 950;
  text-decoration: none;

  &:hover {
    transform: translateY(-1px);
    filter: brightness(1.04);
  }

  &:focus-visible {
    outline: 2px solid rgba(15, 118, 110, 0.45);
    outline-offset: 3px;
  }
`;

const Loading = styled.div`
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 22px;
  padding: 18px;
  box-shadow: 0 14px 44px rgba(0, 0, 0, 0.07);
  color: #6b7280;
  font-weight: 900;
`;

/* ---------- Modal ---------- */

Modal.setAppElement("#root");

const modalStyles = {
  overlay: { backgroundColor: "rgba(0,0,0,0.85)", zIndex: 1000 },
  content: {
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "min(1050px, 92vw)",
    maxHeight: "90vh",
    padding: 0,
    borderRadius: "22px",
    overflow: "hidden",
    border: "none",
    background: "#000",
  },
};

const ModalTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  background: rgba(11, 18, 32, 0.92);
  color: #fff;
`;

const ModalTitle = styled.div`
  font-weight: 950;
  font-size: 14px;
  opacity: 0.95;
`;

const CloseBtn = styled.button`
  appearance: none;
  border: 1px solid rgba(255, 255, 255, 0.18);
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
  border-radius: 12px;
  padding: 8px 10px;
  cursor: pointer;
  font-weight: 950;

  &:hover {
    background: rgba(255, 255, 255, 0.12);
  }
`;

const ModalImg = styled.img`
  width: 100%;
  height: auto;
  display: block;
`;

/* ---------- Component ---------- */

const FullNews = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [newsItem, setNewsItem] = useState(null);
  const [loading, setLoading] = useState(true);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalSrc, setModalSrc] = useState("");
  const [modalLabel, setModalLabel] = useState("Bild");

  useEffect(() => {
    const fetchNewsItem = async () => {
      try {
        setLoading(true);
        const ref = doc(firestore, "news", id);
        const snap = await getDoc(ref);
        if (snap.exists()) setNewsItem(snap.data());
        else setNewsItem(null);
      } finally {
        setLoading(false);
      }
    };

    fetchNewsItem();
  }, [id]);

  const heroImg = useMemo(() => newsItem?.image1 || "", [newsItem]);

  const openModal = (src, label) => {
    if (!src) return;
    setModalSrc(src);
    setModalLabel(label || "Bild");
    setModalOpen(true);
  };

  if (loading) {
    return (
      <Page>
        <Container>
          <Loading>Laddar nyheten…</Loading>
        </Container>
      </Page>
    );
  }

  if (!newsItem) {
    return (
      <Page>
        <Container>
          <Loading>Nyheten hittades inte.</Loading>
          <div style={{ marginTop: 12 }}>
            <GhostLink to="/news">Tillbaka till nyheter</GhostLink>
          </div>
        </Container>
      </Page>
    );
  }

  return (
    <>
      <SEO
        title={newsItem.title || "Nyhet"}
        description="Senaste nyheterna från vår klubb"
        keywords="nyheter, hofors bgk"
      />

      <Page>
        <Container>
          <Hero>
            <HeroBg $img={heroImg} />
            <HeroOverlay />
            <HeroInner>
              <Breadcrumbs aria-label="Brödsmulor">
                <Link to="/">Start</Link>
                <span>›</span>
                <Link to="/news">Nyheter</Link>
                <span>›</span>
                <span>{newsItem.title}</span>
              </Breadcrumbs>

              <Title>{newsItem.title}</Title>

              <MetaRow>
                <Chip>Hofors BGK</Chip>
                {newsItem.competitionLink ? <ChipStrong>Resultat finns</ChipStrong> : <Chip>Nyhet</Chip>}
                <Chip>Nyheter</Chip>
              </MetaRow>
            </HeroInner>
          </Hero>

          <Card>
            <Body>
              <Content dangerouslySetInnerHTML={{ __html: newsItem.content || "" }} />

              <MediaCol>
                {newsItem.image1 && (
                  <ImgBtn
                    type="button"
                    onClick={() => openModal(newsItem.image1, "Bild 1")}
                    aria-label="Öppna bild 1"
                  >
                    <Image src={newsItem.image1} alt="Nyhetsbild 1" />
                  </ImgBtn>
                )}

                {newsItem.image2 && (
                  <ImgBtn
                    type="button"
                    onClick={() => openModal(newsItem.image2, "Bild 2")}
                    aria-label="Öppna bild 2"
                  >
                    <Image src={newsItem.image2} alt="Nyhetsbild 2" />
                  </ImgBtn>
                )}

                {(newsItem.image1 || newsItem.image2) && (
                  <MediaHint>Klicka på en bild för att förstora.</MediaHint>
                )}
              </MediaCol>
            </Body>

            <Actions>
              <ActionsLeft>
                <Btn type="button" onClick={() => navigate(-1)}>
                  ← Tillbaka
                </Btn>
                <GhostLink to="/news">Alla nyheter</GhostLink>
              </ActionsLeft>

              {newsItem.competitionLink && (
                <PrimaryLink
                  href={newsItem.competitionLink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Resultat
                </PrimaryLink>
              )}
            </Actions>
          </Card>
        </Container>
      </Page>

      <Modal
        isOpen={modalOpen}
        onRequestClose={() => setModalOpen(false)}
        style={modalStyles}
        contentLabel={modalLabel}
      >
        <ModalTop>
          <ModalTitle>{modalLabel}</ModalTitle>
          <CloseBtn type="button" onClick={() => setModalOpen(false)}>
            Stäng ✕
          </CloseBtn>
        </ModalTop>
        <ModalImg src={modalSrc} alt={modalLabel} />
      </Modal>
    </>
  );
};

export default FullNews;

